// A record as text a chess program reads: PGN for the whole game (with the
// recorded clocks as `%clk` comments, which lichess and others understand),
// FEN for one position.
import { toFen } from '../game/fen.js';
import { toPgn, type PgnHeaders } from '../game/pgn.js';
import { playerLabel } from './labels.js';
import { recordState } from './record.js';
import type { GameRecord, RecordedMove } from './types.js';

const SITE = 'https://bngames.online/chess/';
const EVENT: Readonly<Record<GameRecord['mode'], string>> = {
  bot: 'Game against the bot',
  local: 'Casual game',
  online: 'Online game',
};

/** How long a download's blob URL is kept alive after the click. */
const REVOKE_DELAY_MS = 10_000;

const pad = (n: number): string => String(n).padStart(2, '0');

/** PGN's date form, in the player's own time zone. */
function pgnDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

/** `%clk` wants h:mm:ss — the time the mover had left after the move. */
function clockComment(move: RecordedMove, mover: 'w' | 'b'): string | null {
  if (move.clocks === null) return null;
  const seconds = Math.max(0, Math.floor(move.clocks[mover] / 1000));
  return `[%clk ${Math.floor(seconds / 3600)}:${pad(Math.floor(seconds / 60) % 60)}:${pad(seconds % 60)}]`;
}

function headers(record: GameRecord): PgnHeaders {
  return {
    Event: EVENT[record.mode],
    Site: SITE,
    Date: pgnDate(record.createdAt),
    Round: '-',
    White: playerLabel(record, 'w'),
    Black: playerLabel(record, 'b'),
    // PGN's own vocabulary: anything decided on the board or by the players is "normal".
    Termination: record.termination === 'flag' ? 'time forfeit' : 'normal',
  };
}

/** The whole game as PGN, or null if the record does not replay. */
export function recordPgn(record: GameRecord): string | null {
  const state = recordState(record);
  if (state === null) return null;
  const whiteFirst = state.startFen.split(/\s+/)[1] !== 'b';
  const comments = record.moves.map((move, i) => clockComment(move, (i % 2 === 0) === whiteFirst ? 'w' : 'b'));
  return toPgn(state, headers(record), comments);
}

/** The final position as FEN, or null if the record does not replay. */
export function recordFen(record: GameRecord): string | null {
  const state = recordState(record);
  return state === null ? null : toFen(state.position);
}

export function pgnFilename(record: GameRecord): string {
  const d = new Date(record.finishedAt);
  return `bn-chess-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${record.id.slice(0, 8)}.pgn`;
}

/** Hand a text file to the browser's download flow. */
export function downloadText(filename: string, text: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Not at once: WebKit has cancelled downloads whose URL was revoked in the same task.
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}
